/**
 * Display preferences (text size, density, reading width), stored per browser.
 *
 *   import displaySettings from '@/utils/displaySettings';
 *   displaySettings.apply();                       // main.js, before mount
 *   displaySettings.get();                         // { textSize: 'm', density: 'comfortable', readingWidth: 'standard' }
 *   displaySettings.set({ textSize: 'l' });        // persists + applies, returns the new settings
 *
 * Applied as overrides on <html> (inline style beats the :root token rules):
 *   --ui-scale            text scale factor; also drives the root font-size so
 *                         every rem-based type token (--text-*) scales with it
 *   --density             spacing factor; the --space-* tokens are re-derived
 *   --content-max-width   reading column width (and --composer-max-width, so the
 *                         composer keeps lining up with the column)
 * plus data-text-size / data-density / data-reading-width attributes for any
 * component that needs a discrete hook. A `camgpt-display-changed` window event
 * (detail = settings) fires after every apply; other tabs follow via `storage`.
 */

const STORAGE_KEY = 'camgpt-display';

export const TEXT_SIZES = [
  { value: 's', label: 'S', name: 'Small', scale: 0.9 },
  { value: 'm', label: 'M', name: 'Default', scale: 1 },
  { value: 'l', label: 'L', name: 'Large', scale: 1.1 },
  { value: 'xl', label: 'XL', name: 'Extra large', scale: 1.22 },
];

export const DENSITIES = [
  { value: 'compact', label: 'Compact', factor: 0.8 },
  { value: 'comfortable', label: 'Comfortable', factor: 1 },
  { value: 'spacious', label: 'Spacious', factor: 1.2 },
];

export const READING_WIDTHS = [
  { value: 'narrow', label: 'Narrow', px: 640 },
  { value: 'standard', label: 'Standard', px: 768 },
  { value: 'wide', label: 'Wide', px: 960 },
];

export const DEFAULTS = Object.freeze({ textSize: 'm', density: 'comfortable', readingWidth: 'standard' });

// Base values of the --space-* tokens (_base-tokens.css); density rescales them.
const SPACE_TOKENS = {
  '--space-2xs': 2,
  '--space-xs': 4,
  '--space-sm': 8,
  '--space-md': 12,
  '--space-lg': 16,
  '--space-xl': 20,
  '--space-2xl': 28,
  '--space-3xl': 40,
  '--space-4xl': 64,
};

const OVERRIDDEN = ['--ui-scale', '--density', '--content-max-width', '--composer-max-width', ...Object.keys(SPACE_TOKENS)];

function find(list, value) {
  return list.find((o) => o.value === value);
}

function normalize(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    textSize: find(TEXT_SIZES, src.textSize) ? src.textSize : DEFAULTS.textSize,
    density: find(DENSITIES, src.density) ? src.density : DEFAULTS.density,
    readingWidth: find(READING_WIDTHS, src.readingWidth) ? src.readingWidth : DEFAULTS.readingWidth,
  };
}

/** Current settings (defaults for anything missing or invalid). */
export function get() {
  try {
    return normalize(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch (e) {
    return { ...DEFAULTS };
  }
}

/** Apply `settings` (default: the stored ones) to <html>. Returns what was applied. */
export function apply(settings) {
  const s = normalize(settings || get());
  if (typeof document === 'undefined') return s;
  const root = document.documentElement;
  const style = root.style;
  OVERRIDDEN.forEach((prop) => style.removeProperty(prop));
  style.removeProperty('font-size');

  const scale = find(TEXT_SIZES, s.textSize).scale;
  const density = find(DENSITIES, s.density).factor;
  const width = find(READING_WIDTHS, s.readingWidth).px;

  style.setProperty('--ui-scale', String(scale));
  style.setProperty('--density', String(density));
  if (scale !== 1) style.setProperty('font-size', `${(scale * 100).toFixed(2)}%`);
  if (density !== 1) {
    Object.entries(SPACE_TOKENS).forEach(([prop, px]) => {
      style.setProperty(prop, `${Math.max(1, Math.round(px * density))}px`);
    });
  }
  if (s.readingWidth !== DEFAULTS.readingWidth) {
    style.setProperty('--content-max-width', `${width}px`);
    style.setProperty('--composer-max-width', `${width}px`);
  }

  root.setAttribute('data-text-size', s.textSize);
  root.setAttribute('data-density', s.density);
  root.setAttribute('data-reading-width', s.readingWidth);

  try {
    window.dispatchEvent(new CustomEvent('camgpt-display-changed', { detail: { ...s } }));
  } catch (e) { /* very old browsers */ }
  return s;
}

/** Merge `patch` into the stored settings, persist, apply. Returns the new settings. */
export function set(patch) {
  const next = normalize({ ...get(), ...(patch || {}) });
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) { /* storage disabled: still apply for this session */ }
  return apply(next);
}

/** Back to defaults. */
export function reset() {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  return apply({ ...DEFAULTS });
}

let syncing = false;
/** Follow changes made in other tabs (idempotent). */
export function syncAcrossTabs() {
  if (syncing || typeof window === 'undefined') return;
  syncing = true;
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) apply();
  });
}

const displaySettings = { get, set, apply, reset, syncAcrossTabs, TEXT_SIZES, DENSITIES, READING_WIDTHS, DEFAULTS };
export default displaySettings;
